import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Activity, Zap, TrendingUp, AlertCircle, X, Search, Sparkles, Megaphone, Gauge, Shield, Lightbulb } from 'lucide-react';
import { getMyDiagnostics } from '../../api/diagnosticsApi';
import { DiagnosticsResponse } from '../../types/diagnostics';
import Spinner from '../../components/Spinner';
import AvatarIndicator from '../../components/AvatarIndicator';

export default function DiagnosticsOverview() {
  const navigate = useNavigate();
  const [latestDiagnostic, setLatestDiagnostic] = useState<DiagnosticsResponse | null>(null); // Renamed 'data' for clarity
  const [recentDiagnostics, setRecentDiagnostics] = useState<DiagnosticsResponse[] | null>(null); // New state for the list of all diagnostics
  const [selectedDiagnostic, setSelectedDiagnostic] = useState<DiagnosticsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const result = await getMyDiagnostics();
        if (Array.isArray(result) && result.length > 0) {
          // Map PascalCase JSON strings from API to camelCase objects for UI
          const mappedResult = result.map((d: any) => {
            const parsedScores = d.categoryScoresJson ? JSON.parse(d.categoryScoresJson) : [];
            return {
              ...d,
              categoryScores: parsedScores.map((s: any) => ({
                category: s.Category || s.category,
                percentage: s.Percentage || s.percentage,
                issues: s.Issues || s.issues || []
              })),
              summaryReport: d.summaryReportJson ? JSON.parse(d.summaryReportJson) : [],
              recommendations: d.recommendationsJson ? JSON.parse(d.recommendationsJson) : [],
              ctaServices: d.ctaServicesJson ? JSON.parse(d.ctaServicesJson) : []
            };
          });
          setLatestDiagnostic(mappedResult[0]);
          setRecentDiagnostics(mappedResult);
        } else {
          setLatestDiagnostic(null); // No data found
          setRecentDiagnostics([]);
        }
      } catch (err: any) {
        setError("Failed to load dashboard data. Please try again later.");
        setLatestDiagnostic(null);
        setRecentDiagnostics([]);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Calculate total issues for the LATEST diagnostic
  const totalIssues = latestDiagnostic?.categoryScores?.reduce((acc, cat) => acc + (cat.issues?.length || 0), 0) || 0;

  const stats = [
    {
      icon: Activity,
      label: 'System Health',
      value: latestDiagnostic?.grade || 'N/A',
      color: 'emerald',
      description: 'Based on last analysis',
    },
    {
      icon: Zap,
      label: 'Performance',
      value: latestDiagnostic ? `${latestDiagnostic.overallPercentage}%` : '0%',
      color: 'blue',
      description: 'Overall optimization score',
    },
    {
      icon: TrendingUp,
      label: 'Uptime',
      value: '99.9%', // Mocked or from generic system health
      color: 'indigo',
      description: 'Network availability',
    },
    {
      icon: AlertCircle,
      label: 'Total Issues',
      value: totalIssues.toString(),
      color: totalIssues > 0 ? 'amber' : 'emerald',
      description: 'Findings requiring review',
    },
  ];

  const formatCreatedDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50">
        <Spinner size="h-12 w-12" />
        <p className="mt-4 text-slate-500 animate-pulse font-medium">Gathering your latest metrics...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-10 p-6 md:p-8 lg:p-10 md:ml-0">
      {/* Hero Section */}
      <div className="mb-8 animate-fade-in">
        <div className="rounded-lg bg-gradient-to-r from-blue-600 to-indigo-600 p-8 md:p-12 text-white shadow-lg">
          <h1 className="text-4xl md:text-5xl font-bold mb-2 tracking-tight">Diagnostic Dashboard</h1>
          <p className="text-blue-100 text-lg md:text-xl mb-6">
            Here's an overview of your system diagnostics and performance metrics.
          </p>
          {/* <button
            onClick={() => navigate('/diagnostics/website-tool')}
            className="inline-flex items-center gap-2 px-6 py-3 bg-white text-blue-600 font-semibold rounded-lg hover:bg-blue-50 transition-all duration-200 shadow-md hover:shadow-lg active:scale-95"
          >
            <Plus size={18} />
            Start New Analysis
          </button> */}
        </div>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-100 text-red-600 rounded-lg flex items-center justify-between animate-shake">
          <div className="flex items-center gap-2">
            <AlertCircle size={20} />
            <span className="font-medium">{error}</span>
          </div>
          <button onClick={() => setError(null)} className="text-red-400 hover:text-red-600 text-xl font-bold">×</button>
        </div>
      )}

      {/* Stats Grid */}
      {latestDiagnostic && ( // Only show stats if there's a latestDiagnostic
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {stats.map((stat, idx) => {
          const Icon = stat.icon;
          const colorClasses: Record<string, { bg: string; icon: string; border: string }> = {
            blue: { bg: 'bg-blue-50', icon: 'text-blue-600', border: 'border-blue-200' },
            emerald: { bg: 'bg-emerald-50', icon: 'text-emerald-600', border: 'border-emerald-200' },
            indigo: { bg: 'bg-indigo-50', icon: 'text-indigo-600', border: 'border-indigo-200' },
            amber: { bg: 'bg-amber-50', icon: 'text-amber-600', border: 'border-amber-200' },
          };

          const colors = colorClasses[stat.color as keyof typeof colorClasses];

          return (
            <div
              key={idx}
              className={`rounded-xl border ${colors.border} p-6 bg-white shadow-sm hover:shadow-md transition-all duration-300 hover:border-blue-300 animate-slide-in-left`}
              style={{ animationDelay: `${idx * 100}ms` }}
            >
              <div className={`inline-flex items-center justify-center w-12 h-12 rounded-lg ${colors.bg} mb-4`}>
                <Icon className={`w-6 h-6 ${colors.icon}`} />
              </div>
              <p className="text-sm font-medium text-slate-600 mb-1">{stat.label}</p>
              <p className={`text-3xl font-extrabold text-slate-900 mb-1`}>{stat.value}</p>
              <p className="text-xs text-slate-400">{stat.description}</p>
            </div>
          );
        })}
        </div>
      )}

      {/* Recent Website Analyses Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 animate-fade-in" style={{ animationDelay: '400ms' }}>
        {/* Recent Website Analyses List */}
        <div className="lg:col-span-2 rounded-lg border border-slate-200 p-6 bg-white shadow-sm">
          <h2 className="text-xl font-bold text-slate-900 mb-4">Recent Website Analyses</h2>
          <div className="space-y-3">
            {recentDiagnostics && recentDiagnostics.length > 0 ? (
              recentDiagnostics.slice(0, 3).map((diagnostic, idx) => (
              <div
                key={diagnostic.id || idx}
                className="flex items-center justify-between p-4 rounded-xl bg-slate-50 hover:bg-white hover:shadow-md hover:border-blue-100 transition-all duration-200 border border-slate-100 group cursor-pointer"
                onClick={() => {
                  setSelectedDiagnostic(diagnostic);
                  // setExpandedIssue(null);
                }}
              >
                <div>
                  <p className="font-bold text-slate-800 group-hover:text-blue-600 transition-colors">{diagnostic.url}</p>
                  <p className="text-xs text-slate-400">
                    Last run: {formatCreatedDate(diagnostic.createdDate)}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`text-sm font-bold ${diagnostic.overallPercentage >= 80 ? 'text-emerald-600' : 'text-amber-600'}`}>
                    {diagnostic.overallPercentage}%
                  </span>
                  <span
                    className={`px-3 py-1 rounded-full text-xs font-medium ${
                      diagnostic.overallPercentage >= 80
                        ? 'bg-emerald-100 text-emerald-700'
                        : diagnostic.overallPercentage >= 60 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'
                    }`}
                  >
                    {diagnostic.grade}
                  </span>
                </div>
              </div>
              ))
            ) : (
              <p className="text-center py-10 text-slate-400 italic">No recent website analyses found. Start a new one!</p>
            )}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="rounded-lg border border-slate-200 p-6 bg-white shadow-sm h-fit">
          <h2 className="text-xl font-bold text-slate-900 mb-4">Quick Actions</h2>
          <div className="space-y-3">
            <button
              onClick={() => navigate('/diagnostics/website-tool')}
              className="w-full px-4 py-2.5 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-all duration-200 active:scale-95 shadow-sm hover:shadow-md flex items-center gap-2">
              <Search size={18} />
              New Website Diagnostic
            </button>
            <button 
              onClick={() => navigate('/diagnostics/marketing-tool')}
              className="w-full px-4 py-2.5 border border-slate-300 text-slate-700 font-medium rounded-lg hover:bg-slate-50 transition-all duration-200 active:scale-95 flex items-center gap-2">
              <Zap size={18} />
              New Marketing Diagnostic
            </button>
            <button 
              onClick={() => navigate('/diagnostics/business-assessment')}
              className="w-full px-4 py-2.5 border border-slate-300 text-slate-700 font-medium rounded-lg hover:bg-slate-50 transition-all duration-200 active:scale-95 flex items-center gap-2 text-left">
              <Activity size={18} className="shrink-0" />
              New Business Health & Strategy Assessment
            </button>
          </div>
        </div>
      </div>

      {/* Results Modal */}
      {selectedDiagnostic && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 sm:p-6 md:p-10">
          <div 
            className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm animate-fade-in" 
            onClick={() => setSelectedDiagnostic(null)}
          />
          <div className="relative w-full max-w-5xl max-h-full bg-slate-50 rounded-2xl shadow-2xl overflow-hidden flex flex-col animate-in zoom-in-95 duration-200">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 md:p-6 border-b bg-white">
              <div>
                <h2 className="text-xl font-bold text-slate-900">Diagnostic Report</h2>
                <p className="text-sm text-slate-500">{selectedDiagnostic.url}</p>
              </div>
              <button 
                onClick={() => setSelectedDiagnostic(null)}
                className="p-2 hover:bg-slate-100 rounded-full transition-colors"
                aria-label="Close modal"
              >
                <X size={24} className="text-slate-500" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="flex-1 overflow-y-auto p-6 md:p-8 space-y-8">
              {/* 1. Top Section: Hero Bento Grid & Overall Health - Full Width Stack */}
              <div className="flex flex-col gap-6">
                {/* Main Visualizer Card */}
                <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-8 flex flex-row items-center justify-center gap-8 relative overflow-hidden group min-h-[300px]">
                  <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500"></div>
                  
                  {/* Left Side: Avatar Indicator */}
                  <div className="flex-shrink-0">
                    <AvatarIndicator state={selectedDiagnostic.visualIndicator} />
                  </div>

                  {/* Right Side: Score Result */}
                  <div className="flex flex-col items-center md:items-start text-left">
                    <div className="bg-slate-50/50 backdrop-blur-sm px-8 py-6 rounded-[2.5rem] border border-slate-100 shadow-inner transition-all group-hover:bg-white group-hover:shadow-md">
                        <span className="text-7xl font-black text-slate-900 tracking-tighter leading-none block">
                          {selectedDiagnostic.overallPercentage}<span className="text-3xl text-indigo-600 font-bold ml-1">%</span>
                        </span>
                        <span className="text-[11px] font-bold text-slate-400 uppercase tracking-[0.2em] mt-3 block">
                          Overall Health
                        </span>
                    </div>
                  </div>
                </div>

                {/* Metric Tiles */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {(selectedDiagnostic.categoryScores || []).map((cat, i) => {
                    const Icon = [Sparkles, Megaphone, Gauge, Shield][i % 4];
                    const color = ["#4f46e5", "#db2777", "#10b981", "#ea580c"][i % 4];
                    return (
                      <div key={cat.category} className="bg-white rounded-3xl border border-slate-100 shadow-sm p-6 flex flex-col justify-between hover:border-blue-200 transition-all group text-left">
                        <div className="flex justify-between items-start mb-4">
                          <div className="p-3 rounded-2xl bg-slate-50 group-hover:bg-white transition-colors border border-transparent group-hover:border-slate-100" style={{ color }}>
                            <Icon size={24} />
                          </div>
                          <span className="text-4xl font-black tracking-tighter" style={{ color }}>{cat.percentage}%</span>
                      </div>
                        <div>
                          <h4 className="text-sm font-bold text-slate-800 uppercase tracking-tight mb-2">{cat.category}</h4>
                          <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
                            <div className="h-full rounded-full transition-all duration-1000" style={{ width: `${cat.percentage}%`, backgroundColor: color }}></div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* 2. Key Improvement Opportunities */}
              <div className="bg-indigo-50/50 rounded-3xl border border-indigo-100 p-8 shadow-sm text-left">
                <h4 className="text-xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                  <div className="p-2 bg-indigo-600 rounded-lg text-white">
                    <Lightbulb size={20} />
                  </div>
                  Key Improvement Opportunities
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {(selectedDiagnostic.recommendations || []).map((rec, i) => (
                    <div key={i} className="flex items-start gap-4 p-5 bg-white rounded-2xl border border-indigo-50 shadow-sm">
                      <span className="flex-shrink-0 w-8 h-8 rounded-full bg-indigo-600 text-white flex items-center justify-center font-bold text-sm">
                        {i + 1}
                      </span>
                      <p
                        className="text-slate-700 text-sm font-normal leading-relaxed text-left"
                        dangerouslySetInnerHTML={{ __html: rec }}
                      ></p>
                    </div>
                  ))}
                </div>
              </div>

              {/* 3. Mid CTA Section */}
              <div className="relative bg-gradient-to-br from-blue-600 to-indigo-800 rounded-[2.5rem] p-12 text-white text-center shadow-2xl overflow-hidden group">
                <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10 pointer-events-none"></div>
                <div className="relative z-10">
                  <h3 className="text-3xl font-bold mb-4 tracking-tight">Ready to Take Your Website Further?</h3>
                  <p className="text-blue-100 text-lg leading-relaxed max-w-2xl mx-auto mb-8 font-normal">
                    Whether you want a faster website, a stronger branding, or a smoother customer experience, 
                    we're here to help turn insights into real improvements.
                  </p>
                  <button className="px-10 py-4 bg-white text-blue-700 font-bold rounded-full hover:shadow-xl hover:scale-105 transition-all active:scale-95">
                    Contact Us
                  </button>
                </div>
              </div>

              {/* 4. Report Summary Section */}
              <div className="bg-white rounded-3xl border border-slate-200 p-10 shadow-sm relative text-left">
                <h3 className="text-2xl font-bold text-slate-900 mb-8 flex items-center gap-3">
                  <Megaphone size={22} className="text-indigo-600" />
                  Report Summary
                </h3>
                <div className="space-y-6 text-left border-l-4 border-indigo-100 pl-8 ml-2">
                  {(selectedDiagnostic.summaryReport || []).map((para, i) => (
                    <p
                      key={i}
                      className="text-slate-600 leading-relaxed text-md font-normal"
                      dangerouslySetInnerHTML={{ __html: para }}
                    />
                  ))}
                </div>
              </div>

              {/* 5. Unlock Full Report CTA */}
              <div className="bg-slate-950 rounded-[2.5rem] border-none shadow-2xl p-12 text-white text-center relative overflow-hidden">
                <div className="absolute top-0 right-0 -mt-20 -mr-20 w-80 h-80 bg-blue-600/20 rounded-full blur-[100px]"></div>
                <div className="absolute bottom-0 left-0 -mb-20 -ml-20 w-80 h-80 bg-indigo-600/10 rounded-full blur-[100px]"></div>
                <div className="relative z-10">
                  <h3 className="text-3xl font-bold mb-4">Unlock the Full Website Health Report</h3>
                  <p className="text-slate-400 leading-relaxed max-w-2xl mx-auto mb-10 text-lg">
                    Get the complete breakdown with detailed findings, improvement recommendations, and actionable insights.
                  </p>
                  <button className="px-12 py-5 bg-blue-600 text-white font-bold rounded-full hover:bg-blue-500 shadow-2xl shadow-blue-600/20 transition-all transform hover:-translate-y-1">
                    Buy Now
                  </button>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="p-4 md:p-6 border-t bg-white flex justify-end">
              <button 
                onClick={() => setSelectedDiagnostic(null)}
                className="btn-secondary px-6 py-2"
              >
                Close Report
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}